import { useState } from "react";
import { FileText, X, Eye, ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import { UploadMetadata, PresignedUrl } from "../../interfaces/chat.interface";
import useChatApiService from "@/services/chat.service";

/**
 * Props for the FileAttachment component
 */
interface FileAttachmentProps {
  /** File object to display (for local files) */
  file?: File;
  /** Upload metadata to display (for uploaded files) */
  upload?: UploadMetadata;
  /** Callback function called when the file is removed */
  onRemove?: () => void;
  /** Whether to show the remove button. Defaults to false. */
  showRemove?: boolean;
  /** Whether to show the download button. Defaults to false. */
  showDownload?: boolean;
}

/**
 * FileAttachment component displays a file attachment with file type icon, name, and size.
 * Supports both local File objects and uploaded files (UploadMetadata). Can show
 * remove and download buttons. Handles file name truncation and size formatting.
 * 
 * @component
 * @param {FileAttachmentProps} props - Component props
 * @param {File} [props.file] - Local file object
 * @param {UploadMetadata} [props.upload] - Upload metadata
 * @param {() => void} [props.onRemove] - Callback fired when file is removed
 * @param {boolean} [props.showRemove=false] - Whether to show remove button
 * @param {boolean} [props.showDownload=false] - Whether to show download button
 * @returns {JSX.Element} File attachment card with icon, name, size, and action buttons
 * 
 * @example
 * ```tsx
 * <FileAttachment 
 *   file={file}
 *   showRemove={true}
 *   onRemove={handleRemove}
 * />
 * ```
 */
export const FileAttachment: React.FC<FileAttachmentProps> = ({
  file,
  upload,
  onRemove,
  showRemove = false,
  showDownload = false
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { getPresignedUrl } = useChatApiService();


  const getFileTypeLabel = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toUpperCase();
    return extension || "FILE";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const truncateFileName = (name: string, maxLength: number = 25) => {
    if (name.length <= maxLength) return name;
    const extension = name.split(".").pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf("."));
    const truncated = nameWithoutExt.substring(
      0,
      maxLength - 3 - (extension?.length || 0)
    );
    return `${truncated}...${extension}`;
  };

  const fileName = file ? file.name : upload?.original_filename || "Unknown";
  const fileSize = file ? file.size : upload?.file_size_bytes || 0;

  const handleDownload = async () => {
    if (!upload?.blob_url) return;

    setIsDownloading(true);
    try {
      const payload: PresignedUrl = {
        file_path: upload.blob_url,
        expiry_hours: 2,
        permission: "r",
      };

      const { presigned_url } = await getPresignedUrl("uploads", payload);
      if (!presigned_url) throw new Error("No presigned URL returned");

      const link = document.createElement("a");
      link.href = presigned_url;
      link.target = "_blank";
      link.rel = "noopener";
      link.download =
        upload.original_filename ||
        upload.blob_url.split("/").pop() ||
        "downloaded-file";
    
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Download failed:", error);
      if (upload?.blob_url) window.open(upload.blob_url, "_blank");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2 bg-[#EEF2F6] border border-[#DFEAFA] rounded-lg px-2 py-2 text-sm max-w-full">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="flex flex-col items-center justify-center rounded flex-shrink-0">
          <FileText size={20} className="text-black" />
          <span className="text-[8px] pt-0.5 font-semibold text-black">
            {getFileTypeLabel(fileName)}
          </span>
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-black font-medium truncate text-xs">
            {truncateFileName(fileName)}
          </span>
          <span className="text-[10px] text-black">
            {formatFileSize(fileSize)}
          </span>
        </div>
      </div>

      {showDownload && upload && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          aria-label={`Download ${fileName}`}
          className="h-5 w-5 p-0 hover:bg-gray-200 rounded-full flex-shrink-0"
          disabled={isDownloading}
        >
          <ArrowUpRight size={12} className="text-gray-500" />
        </Button>
      )}

      {showRemove && onRemove && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label={`Remove ${fileName}`}
          className="h-5 w-5 p-0 hover:bg-gray-200 rounded-full flex-shrink-0"
        >
          <X size={12} className="text-gray-500" />
        </Button>
      )}
    </div>
  );
};
